import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { heatLayer } from "@linkurious/leaflet-heat";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getQuizHeatmapCall } from "../../redux/api/quizSystem.api";
import ProfSelector from "components/ProfSelector";

function ensureTileUrl(url) {
  if (!url || typeof url !== "string") return "";
  const u = url.trim();
  if (u.includes("{z}") && u.includes("{x}") && u.includes("{y}")) return u;
  const base = u.replace(/\/*\{z\}\/\*\{x\}\/\*\{y\}\.png$/i, "").replace(/\/+$/, "");
  return `${base}/{z}/{x}/{y}.png`;
}

// Same bounds as the slide map (Leaflet tile layer).
const FULL_SLIDE_BOUNDS = { minLat: -85, maxLat: 85, minLng: -180, maxLng: 180 };

const INTENSITY_WRONG = 1;
const INTENSITY_CORRECT = 0.15;

/**
 * Aggregate raw answer points by pin location so each pin has one heat value based on
 * correct vs wrong answers at that pin. E.g. 4 pins on a slide (1 from one question,
 * 3 from another) → 4 points, each colored by that pin's correctness.
 * points: [lat, lng, intensity][] (intensity 1 = wrong, 0.15 = correct).
 * Returns [lat, lng, wrongness][] with wrongness in [0, 1] (0 = blue = all correct, 1 = red = all wrong).
 */
function aggregatePointsByPin(points, locationDecimals = 6) {
  if (!points.length) return [];
  const round = (v) => Math.round(v * 10 ** locationDecimals) / 10 ** locationDecimals;
  const groups = new Map();
  for (const p of points) {
    const lat = p[0];
    const lng = p[1];
    const intensity = typeof p[2] === "number" ? p[2] : INTENSITY_WRONG;
    const key = `${round(lat)}_${round(lng)}`;
    if (!groups.has(key)) {
      groups.set(key, { lat: 0, lng: 0, sum: 0, count: 0 });
    }
    const g = groups.get(key);
    g.lat += lat;
    g.lng += lng;
    g.sum += intensity;
    g.count += 1;
  }
  const result = [];
  for (const g of groups.values()) {
    const lat = g.lat / g.count;
    const lng = g.lng / g.count;
    const avgIntensity = g.sum / g.count;
    const wrongness = Math.max(
      0,
      Math.min(1, (avgIntensity - INTENSITY_CORRECT) / (INTENSITY_WRONG - INTENSITY_CORRECT))
    );
    result.push([lat, lng, wrongness]);
  }
  return result;
}

export default function HeatMap() {
  const [slides, setSlides] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProfId, setSelectedProfId] = useState("");
  const mapRef = useRef(null);
  const heatLayerRef = useRef(null);
  const containerId = "heatmap-slide-map";

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = selectedProfId ? { prof_id: selectedProfId } : {};
    getQuizHeatmapCall(params)
      .then((res) => {
        if (!mounted || res?.data?.slides == null) return;
        setSlides(res.data.slides || []);
        setSelectedIndex(null);
      })
      .catch(() => {
        if (mounted) setSlides([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [selectedProfId]);

  const selectedSlide = selectedIndex != null ? slides[selectedIndex] : null;
  const tileUrl = selectedSlide ? ensureTileUrl(selectedSlide.question_image_url) : null;
  const points = selectedSlide?.points || [];

  useEffect(() => {
    let timeoutId = null;
    let mounted = true;
    if (!tileUrl || !points.length) {
      if (mapRef.current) {
        if (heatLayerRef.current) {
          mapRef.current.removeLayer(heatLayerRef.current);
          heatLayerRef.current = null;
        }
        mapRef.current.remove();
        mapRef.current = null;
      }
      return () => { mounted = false; };
    }
    const el = document.getElementById(containerId);
    if (!el) return () => { mounted = false; };

    if (mapRef.current) {
      if (heatLayerRef.current) {
        mapRef.current.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(containerId, {
      maxZoom: 10,
      maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      maxBoundsViscosity: 1,
    }).setView([-10.505, -0.09], 2);

    L.tileLayer(tileUrl, {
      attribution: "&copy; Microlab",
      maxZoom: 10,
      noWrap: true,
      bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
    }).addTo(map);

    mapRef.current = map;

    if (points.length >= 1) {
      const lats = points.map((p) => p[0]);
      const lngs = points.map((p) => p[1]);
      const bounds = L.latLngBounds(
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)]
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10, animate: false });
      }
    }

    const addHeat = () => {
      if (!mounted || !mapRef.current) return;
      if (!document.getElementById(containerId)) return;
      mapRef.current.invalidateSize();
      const pinPoints = aggregatePointsByPin(points);
      try {
        const heat = heatLayer(pinPoints, {
          radius: 38,
          blur: 32,
          maxZoom: 10,
          minOpacity: 0.5,
          max: 1,
          gradient: {
            0: "blue",
            0.2: "rgb(0, 180, 220)",
            0.4: "cyan",
            0.55: "lime",
            0.7: "yellow",
            0.85: "orange",
            1: "red",
          },
        });
        heat.addTo(mapRef.current);
        heatLayerRef.current = heat;
      } catch (err) {
        const circlesLayer = L.layerGroup();
        pinPoints.forEach((p) => {
          const lat = p[0];
          const lng = p[1];
          const wrongness = typeof p[2] === "number" ? p[2] : 0.5;
          const isWrong = wrongness > 0.5;
          L.circleMarker([lat, lng], {
            radius: isWrong ? 14 : 10,
            fillColor: isWrong ? "#e53935" : "#1e88e5",
            color: isWrong ? "#b71c1c" : "#0d47a1",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.6,
          }).addTo(circlesLayer);
        });
        circlesLayer.addTo(mapRef.current);
        heatLayerRef.current = circlesLayer;
      }
    };

    timeoutId = setTimeout(addHeat, 150);

    return () => {
      mounted = false;
      if (timeoutId != null) clearTimeout(timeoutId);
      const map = mapRef.current;
      const heat = heatLayerRef.current;
      heatLayerRef.current = null;
      mapRef.current = null;
      if (heat && map) {
        try { map.removeLayer(heat); } catch (_) {}
      }
      if (map) {
        try { map.remove(); } catch (_) {}
      }
    };
  }, [tileUrl, selectedIndex, slides]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" flexDirection="column" gap={2}>
          <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <ProfSelector value={selectedProfId} onChange={setSelectedProfId} />
          </MDBox>
          <MDTypography variant="h4" fontWeight="medium">
            Quiz heat map
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Heat at every pin on the slide (from all questions): blue = mostly correct, red = mostly wrong. Pins with no answers yet show neutral (yellow/green).
          </MDTypography>
        </MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ overflow: "hidden" }}>
              <MDBox p={2}>
                <MDTypography variant="button" color="text" fontWeight="bold" mb={1}>
                  Slides with quiz data
                </MDTypography>
                {loading ? (
                  <MDTypography variant="caption" color="text">Loading…</MDTypography>
                ) : slides.length === 0 ? (
                  <MDTypography variant="caption" color="text">
                    No slide-based quiz data yet. Use slide-referenced questions in quizzes and have students submit attempts.
                  </MDTypography>
                ) : (
                  <List disablePadding>
                    {slides.map((slide, idx) => (
                      <ListItemButton
                        key={idx}
                        selected={selectedIndex === idx}
                        onClick={() => setSelectedIndex(idx)}
                      >
                        <ListItemText
                          primary={slide.title || `Slide ${idx + 1}`}
                          secondary={`${aggregatePointsByPin(slide.points || []).length} pin(s), ${(slide.points || []).length} answers`}
                          primaryTypographyProps={{ variant: "button" }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card sx={{ overflow: "hidden" }}>
              <MDBox sx={{ position: "relative", height: 500 }}>
                {selectedSlide ? (
                  <div
                    id={containerId}
                    style={{
                      width: "100%",
                      height: 500,
                      minHeight: 500,
                    }}
                  />
                ) : (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    minHeight={500}
                    bgcolor="grey.200"
                  >
                    <MDTypography variant="body2" color="text">
                      Select a slide to view the heat map
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
